import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import Reviews from "@/components/Reviews";
import { ChatWidget } from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Star, Home as HomeIcon, Clock, ShieldCheck, BadgeCheck, MessageSquare, CalendarDays } from "lucide-react";

export default function BusinessProfile() {
  const { id } = useParams();
  const { t, lang, bizName, bizDesc } = useLang();
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(false);
  const [interest, setInterest] = useState("");
  const [lead, setLead] = useState({ name: "", email: "", phone: "", city: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [id]);

  if (b === null) return <div className="py-32 text-center text-slate-400">…</div>;
  if (b === false) return <div className="py-32 text-center text-slate-500">404</div>;

  const sp = b.site_profile || {};
  const money = (p) => (p == null || p === "" ? null : `$${Number(p).toFixed(2)}`);
  const memberYear = (b.created_at || "").slice(0, 4);
  const openRequest = (what) => { setInterest(what || ""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/businesses/${id}/leads`, { ...lead, interest });
      toast.success(lang === "es" ? "¡Solicitud enviada! El negocio te contactará." : "Request sent! The business will contact you.");
      setOpen(false); setLead({ name: "", email: "", phone: "", city: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const ItemCard = ({ it, cta }) => (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid="site-item-card">
      {it.image && <img src={it.image} alt={it.name} className="mb-3 h-36 w-full rounded-lg object-cover" />}
      <p className="font-head font-bold text-secondary">{it.name}</p>
      {it.description && <p className="mt-1 flex-1 text-sm text-slate-600">{it.description}</p>}
      <div className="mt-auto flex items-center justify-between pt-3">
        {money(it.price) ? <span className="font-head text-lg font-extrabold text-secondary">{money(it.price)}</span> : <span className="text-xs font-semibold text-slate-400">{lang === "es" ? "Cotización" : "Quote"}</span>}
        <Button size="sm" className="rounded-full bg-primary hover:bg-orange-600" onClick={() => openRequest(it.name)} data-testid="item-request-btn">{cta}</Button>
      </div>
    </div>
  );

  const TrustPill = ({ Icon, label, value }) => (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent"><Icon className="h-4 w-4" /></span>
      <div className="leading-tight"><p className="text-sm font-bold text-secondary">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
  );

  return (
    <div className="bg-background">
      {/* Hero */}
      <div className="relative h-64 overflow-hidden bg-gradient-to-br from-secondary to-slate-800 md:h-80 lg:h-96">
        {b.cover_image ? <img src={b.cover_image} alt="" className="h-full w-full object-cover" style={{ objectPosition: b.cover_position || "center" }} /> : <div className="flex h-full items-center justify-center text-white/20"><HomeIcon className="h-20 w-20" /></div>}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-secondary via-secondary/50 to-transparent" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header card */}
        <div className="relative z-10 -mt-20 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl md:p-8" data-testid="business-profile-header">
          <div className="flex flex-col gap-5 md:flex-row md:items-center">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
              {b.logo ? <img src={b.logo} alt="logo" className="h-full w-full object-contain" /> : <HomeIcon className="h-9 w-9 text-slate-400" />}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary md:text-4xl">{bizName(b)}</h1>
                <span className="flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1 text-xs font-bold text-accent" data-testid="verified-badge"><BadgeCheck className="h-4 w-4" /> {lang === "es" ? "Verificado" : "Verified"}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-white"><Star className="h-3.5 w-3.5 fill-white" />{b.review_count ? `${b.rating} (${b.review_count})` : (lang === "es" ? "Nuevo" : "New")}</span>
                {(b.city || b.state) && <span className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {b.address ? `${b.address}, ` : ""}{[b.city, b.state].filter(Boolean).join(", ")}</span>}
                {((b.category === "other" && b.custom_category) || b.is_master) && <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-primary" data-testid="profile-custom-cat">{b.is_master ? (lang === "es" ? "Todos los servicios" : "All services") : b.custom_category}</span>}
              </div>
            </div>
            <Button className="rounded-full bg-primary px-6 hover:bg-orange-600 md:self-start" onClick={() => openRequest("")} data-testid="contact-btn">{t("contact")}</Button>
          </div>

          {/* Trust metrics */}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="trust-metrics">
            <TrustPill Icon={Star} label={lang === "es" ? "Calificación de clientes" : "Customer rating"} value={b.review_count ? `${b.rating} · ${b.review_count} ${lang === "es" ? "reseñas" : "reviews"}` : (lang === "es" ? "Profesional nuevo" : "New pro")} />
            <TrustPill Icon={ShieldCheck} label={lang === "es" ? "Pago protegido por URBIS" : "Protected by URBIS"} value={lang === "es" ? "Contacto seguro" : "Secure contact"} />
            <TrustPill Icon={CalendarDays} label={lang === "es" ? "En URBIS" : "On URBIS"} value={memberYear ? `${lang === "es" ? "Desde" : "Since"} ${memberYear}` : (lang === "es" ? "Profesional activo" : "Active pro")} />
          </div>
        </div>

        {/* Body grid */}
        <div className="my-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-8">
            <section>
              <h2 className="font-head text-2xl font-bold text-secondary">{lang === "es" ? "Quiénes somos" : "About us"}</h2>
              <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-600">{sp.about || bizDesc(b) || "—"}</p>
            </section>

            {(sp.services || []).length > 0 && (
              <section data-testid="profile-services">
                <h2 className="font-head text-2xl font-bold text-secondary">{lang === "es" ? "Servicios" : "Services"}</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {sp.services.map((it, i) => <ItemCard key={i} it={it} cta={lang === "es" ? "Solicitar" : "Request"} />)}
                </div>
              </section>
            )}

            {(sp.products || []).length > 0 && (
              <section data-testid="profile-products">
                <h2 className="font-head text-2xl font-bold text-secondary">{lang === "es" ? "Productos" : "Products"}</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {sp.products.map((it, i) => <ItemCard key={i} it={it} cta={sp.allow_online_orders && money(it.price) ? (lang === "es" ? "Comprar" : "Buy") : (lang === "es" ? "Solicitar" : "Request")} />)}
                </div>
              </section>
            )}

            {(b.portfolio || []).length > 0 && (
              <section data-testid="profile-portfolio">
                <h2 className="font-head text-2xl font-bold text-secondary">{lang === "es" ? "Nuestros trabajos" : "Our work"}</h2>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {(b.portfolio || []).map((it) => (
                    <div key={it.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`portfolio-item-${it.id}`}>
                      <img src={it.url} alt={it.caption || "trabajo"} className="h-40 w-full object-cover transition-transform hover:scale-105" />
                      {it.caption && <p className="truncate p-2 text-xs text-slate-600">{it.caption}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(sp.faqs || []).length > 0 && (
              <section data-testid="profile-faqs">
                <h2 className="font-head text-2xl font-bold text-secondary">{lang === "es" ? "Preguntas frecuentes" : "FAQ"}</h2>
                <div className="mt-4 space-y-3">
                  {sp.faqs.map((f, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`faq-${i}`}>
                      <p className="font-semibold text-secondary">{f.q}</p>
                      <p className="mt-1 text-sm text-slate-600">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <Reviews businessId={b.id} />
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-6 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Solicita tu cotización" : "Request a quote"}</h3>
                <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Cuéntale al profesional lo que necesitas. Te contactará pronto." : "Tell the pro what you need. They'll reach out soon."}</p>
                {b.hourly_rate > 0 && <p className="mt-3 font-head text-2xl font-extrabold text-secondary">${b.hourly_rate}<span className="text-sm font-semibold text-slate-400">/hr</span></p>}
                <Button className="mt-4 w-full rounded-full bg-primary hover:bg-orange-600" onClick={() => openRequest("")} data-testid="sidebar-request-btn"><MessageSquare className="mr-2 h-4 w-4" />{lang === "es" ? "Solicitar servicio" : "Request service"}</Button>
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-accent/30 bg-accent/5 px-3 py-2.5 text-xs text-emerald-800" data-testid="secure-note">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  {lang === "es" ? "Pago y contacto protegidos por URBIS. No compartas datos fuera de la plataforma." : "Payment and contact protected by URBIS. Don't share info outside the platform."}
                </div>
              </div>

              {sp.hours && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Clock className="h-4 w-4" />{lang === "es" ? "Horario" : "Hours"}</h3>
                  <p className="whitespace-pre-line text-sm text-slate-700">{sp.hours}</p>
                </div>
              )}

              {(() => {
                const mapCard = (label, q, key) => (
                  <div key={key || "single"} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={key ? `business-branch-${key}` : "business-map-card"}>
                    <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><MapPin className="h-4 w-4" />{label}</h3>
                    <div className="overflow-hidden rounded-xl border border-slate-200">
                      <iframe title={`map-${key || "single"}`} src={`https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`} className="h-40 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="business-map-frame" />
                    </div>
                    <p className="mt-3 text-sm text-slate-600">{q}</p>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-slate-700" data-testid="business-directions-btn"><MapPin className="h-4 w-4" />{lang === "es" ? "Cómo llegar" : "Get directions"}</a>
                  </div>
                );
                const locs = (b.locations || []).filter((l) => l.city || l.address);
                const cards = [];
                if (b.address || b.city) cards.push(mapCard(lang === "es" ? "Ubicación principal" : "Head office", [b.address, b.city, b.state].filter(Boolean).join(", "), "hq"));
                locs.forEach((l, i) => cards.push(mapCard(l.name || (lang === "es" ? `Sucursal ${i + 1}` : `Branch ${i + 1}`), [l.address, l.city, l.state].filter(Boolean).join(", "), l.id || String(i))));
                return cards.length ? cards : null;
              })()}
            </div>
          </aside>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{lang === "es" ? "Solicitar / Contactar" : "Request / Contact"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
            {interest && <p className="rounded-xl bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">{lang === "es" ? "Interés" : "Interest"}: {interest}</p>}
            <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Nombre" : "Name"}</Label><Input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="mt-1 rounded-xl" data-testid="lead-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-slate-500">Email</Label><Input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="mt-1 rounded-xl" data-testid="lead-email" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Teléfono" : "Phone"}</Label><Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="mt-1 rounded-xl" data-testid="lead-phone" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Ciudad" : "City"}</Label><Input value={lead.city} onChange={(e) => setLead({ ...lead, city: e.target.value })} placeholder={lang === "es" ? "¿En qué ciudad necesitas el servicio?" : "Which city do you need service in?"} className="mt-1 rounded-xl" data-testid="lead-city" /></div>
            <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Mensaje" : "Message"}</Label><Textarea required value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="mt-1 rounded-xl" rows={3} data-testid="lead-message" /></div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-primary hover:bg-orange-600" data-testid="lead-submit">{sending ? "…" : (lang === "es" ? "Enviar solicitud" : "Send request")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <ChatWidget businessId={b.id} businessName={bizName(b)} />
    </div>
  );
}
